/**
 * `<ResumeDocument>` — page-scoped renderer for `candidates.resume_html`.
 *
 * Lazy-AI credit model (2026-05-21): the component NO LONGER auto-fires the
 * format mutation on mount. Resume formatting is now triggered either as a
 * side effect of `rank_one`'s first pair (bundled, absorbed into the rank
 * credit) or by the explicit "Format Resume" button rendered inside the
 * `"raw-text"` mode below (metered — consumes 1 credit). The component
 * leans on `useCandidatePolling` to re-fetch the row at ~2500ms while
 * `resume_formatting=TRUE`.
 *
 * **Security carve-out (S-30e-8).** This is the ONE component in the
 * frontend permitted to use `dangerouslySetInnerHTML`. The structural
 * test at `frontend/src/__tests__/no-dangerous-html.test.ts` asserts
 * the attribute appears only here AND the value passed to `__html` is
 * the cached output of a `sanitizeResumeHtml(...)` call.
 *
 * Render modes (4 primary + 2 fallbacks):
 *
 *   Primary (mutually exclusive, matches prototype canon):
 *     - "html"      : `resume_html` non-empty → sanitized + injected
 *     - "spinner"   : `resume_formatting=TRUE` ONLY (a true in-flight
 *                     worker job) → "Formatting resume with AI… You can
 *                     navigate away — this will finish in the background."
 *                     (prototype `lc-wrap`)
 *     - "failed"    : `resume_formatting=FALSE` AND
 *                     `resume_formatting_failed_at IS NOT NULL` →
 *                     empty state with Retry CTA
 *     - "raw-text"  : `text` present, no html, not formatting, not failed
 *                     → the honest pre-format state: raw extracted text
 *                     panel + explicit "Format Resume" button.
 *
 *   Fallback (when no html / no text / not formatting / not failed):
 *     - "file-only" : `storage_key_present=TRUE` → "Preview unavailable"
 *                     panel + Download Original CTA
 *     - "empty"     : truly nothing on file → Back-to-Candidates CTA
 */
import { useMemo, type JSX } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, FileX, AlertCircle, FileText } from "lucide-react";

import type { Candidate } from "@/hooks/queries/candidates/useCandidates";
import { Button } from "@/components/common/Button";
import { useMe } from "@/hooks/queries/auth/useMe";
import { useFormatResumeMutation } from "@/hooks/queries/candidates/useFormatResumeMutation";
import { sanitizeResumeHtml } from "@/lib/sanitize";
import { useTenantV1Base } from "@/hooks/useTenantV1Base";

interface ResumeDocumentProps {
  candidate: Candidate;
}

type Mode = "html" | "spinner" | "failed" | "raw-text" | "file-only" | "empty";

function resolveMode(candidate: Candidate): Mode {
  const html = candidate.resume_html?.trim();
  if (html && html.length > 0) return "html";
  // "spinner" is now reserved for a TRUE in-flight worker job. With
  // auto-fire removed, a text-present-no-html candidate is NOT formatting
  // — rendering a perpetual spinner that never resolves would be a bug.
  if (candidate.resume_formatting === true) return "spinner";
  const hasFailedAt =
    candidate.resume_formatting_failed_at !== null &&
    candidate.resume_formatting_failed_at !== undefined &&
    candidate.resume_formatting_failed_at !== "";
  if (hasFailedAt) return "failed";
  const text = candidate.text?.trim();
  if (text && text.length > 0) {
    // Text present, no html, not formatting, no failed_at: the honest
    // pre-format state. Render the raw extracted text + an explicit
    // "Format Resume" button (the lazy-AI model — formatting is opt-in
    // here, or arrives bundled with a JD pair).
    return "raw-text";
  }
  if (candidate.storage_key_present === true) return "file-only";
  return "empty";
}

export function ResumeDocument({
  candidate,
}: ResumeDocumentProps): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const v1Base = useTenantV1Base();
  const me = useMe();
  // I-KS-2: hide the user-triggered format affordances (the "raw-text"
  // mode Format Resume button + the "failed" mode Retry button) when the
  // backend kill-switch is off. Default-on (treat undefined as enabled)
  // so a missing field on a partial deploy or impersonation envelope does
  // not accidentally suppress the action.
  const resumeFormatEnabled = me.data?.feature_flags?.resume_format !== false;

  const mode = resolveMode(candidate);

  // Sanitize once per `resume_html` value (Architect §13 tip 1).
  const sanitizedHtml = useMemo(() => {
    if (mode !== "html") return "";
    return sanitizeResumeHtml(candidate.resume_html ?? "");
  }, [candidate.resume_html, mode]);

  // The format mutation is no longer auto-fired on mount (lazy-AI model).
  // It is now driven by two explicit user actions: the "failed" mode Retry
  // button and the "raw-text" mode "Format Resume" button.
  const formatMutation = useFormatResumeMutation();

  const handleFormat = (): void => {
    formatMutation.mutate({ candidateId: candidate.id });
  };

  // ── Empty: truly nothing on file ─────────────────────────────────────────
  if (mode === "empty") {
    return (
      <div
        data-testid="resume-document-empty"
        className="flex flex-1 flex-col items-center justify-center px-content-px py-12 text-center"
      >
        <FileX size={48} className="mb-4 text-text-tertiary" aria-hidden />
        <h3 className="mb-1.5 text-base font-semibold text-text-primary">
          No resume on file
        </h3>
        <p className="mb-5 max-w-md text-sm text-text-secondary">
          This candidate doesn&apos;t have a resume yet. Add one from the
          Candidates list to see it here.
        </p>
        {tenantSlug ? (
          <Link to={`${v1Base}/candidates`}>
            <Button variant="secondary">Back to Candidates</Button>
          </Link>
        ) : null}
      </div>
    );
  }

  // ── File-only: storage_key_present but nothing renderable inline ────────
  if (mode === "file-only") {
    return (
      <div
        data-testid="resume-document-file-only"
        className="flex flex-1 flex-col items-center justify-center px-content-px py-12 text-center"
      >
        <Download size={48} className="mb-4 text-text-tertiary" aria-hidden />
        <h3 className="mb-1.5 text-base font-semibold text-text-primary">
          Preview unavailable
        </h3>
        <p className="mb-5 max-w-md text-sm text-text-secondary">
          {candidate.filename
            ? `${candidate.filename} is attached but couldn't be previewed inline. Download to view.`
            : "This resume is attached but couldn't be previewed inline. Download to view."}
        </p>
        {tenantSlug ? (
          <a
            href={`/api/t/${tenantSlug}/candidates/${candidate.id}/resume`}
            {...(candidate.filename ? { download: candidate.filename } : {})}
            data-testid="resume-document-download-original"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-grad px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:shadow-md hover:brightness-110"
          >
            <Download size={14} aria-hidden />
            Download Original
          </a>
        ) : null}
      </div>
    );
  }

  // ── Failed: worker terminated with an error; surface a Retry CTA ─────────
  if (mode === "failed") {
    return (
      <div
        data-testid="resume-document-failed"
        className="flex flex-1 flex-col items-center justify-center px-content-px py-12 text-center"
      >
        <AlertCircle
          size={48}
          className="mb-4 text-text-tertiary"
          aria-hidden
        />
        <h3 className="mb-1.5 text-base font-semibold text-text-primary">
          Formatting failed
        </h3>
        <p className="mb-5 max-w-md text-sm text-text-secondary">
          We couldn&apos;t format this resume. You can try again — this will
          consume 1 credit if the retry succeeds.
        </p>
        {resumeFormatEnabled ? (
          <Button
            variant="primary"
            onClick={handleFormat}
            loading={formatMutation.isPending}
            data-testid="resume-document-retry-button"
          >
            Try Again
          </Button>
        ) : null}
      </div>
    );
  }

  // ── Raw-text: text on file, not yet formatted — the honest pre-format
  // state. Shows the raw extracted text + an explicit "Format Resume"
  // button (metered: consumes 1 credit). ──────────────────────────────────
  if (mode === "raw-text") {
    return (
      <div
        data-testid="resume-document-raw-text"
        className="scroll-primary flex flex-1 flex-col items-center overflow-x-hidden overflow-y-auto px-content-px py-5"
      >
        <div className="flex w-full max-w-resume-doc-w flex-col gap-4">
          <div className="flex flex-col items-center gap-3 rounded-lg border border-border-default bg-surface-raised px-6 py-5 text-center">
            <FileText
              size={32}
              className="text-text-tertiary"
              aria-hidden
            />
            <h3 className="text-base font-semibold text-text-primary">
              Resume not yet formatted
            </h3>
            <p className="max-w-md text-sm text-text-secondary">
              We&apos;re showing the raw extracted text below. Formatting
              happens automatically when you rank this candidate against a
              job — or you can format it now. This will consume 1 credit.
            </p>
            {resumeFormatEnabled ? (
              <Button
                variant="primary"
                onClick={handleFormat}
                loading={formatMutation.isPending}
                data-testid="resume-document-format-button"
              >
                Format Resume
              </Button>
            ) : null}
          </div>
          <pre
            data-testid="resume-document-raw-text-body"
            className="w-full whitespace-pre-wrap break-words rounded-lg border border-border-default bg-surface-base px-5 py-4 font-sans text-sm leading-relaxed text-text-secondary"
          >
            {candidate.text}
          </pre>
        </div>
      </div>
    );
  }

  // ── Spinner: a true in-flight worker job (resume_formatting=TRUE) ───────
  if (mode === "spinner") {
    return (
      <div
        data-testid="resume-document-spinner"
        className="flex flex-1 flex-col items-center justify-center px-content-px py-12 text-center"
      >
        <div
          className="mb-4 size-10 animate-spin rounded-full border-2 border-border-default border-t-accent-secondary"
          aria-hidden
        />
        <h3 className="mb-1.5 text-base font-semibold text-text-primary">
          Formatting resume with AI…
        </h3>
        <p className="max-w-md text-sm text-text-secondary">
          You can navigate away — this will finish in the background.
        </p>
      </div>
    );
  }

  // ── HTML (default mode==='html') ─────────────────────────────────────────
  // Single Download Original button stays in the right-aligned action row.
  // The raw extracted text now has its own first-class "raw-text" mode
  // (rendered above) — it is the pre-format state, distinct from this
  // formatted-HTML mode.
  return (
    <div className="scroll-primary flex flex-1 flex-col items-start overflow-x-hidden overflow-y-auto px-content-px py-5">
      {candidate.storage_key_present && tenantSlug ? (
        <div className="mb-4 flex w-full max-w-resume-doc-w items-center justify-end gap-2">
          <a
            href={`/api/t/${tenantSlug}/candidates/${candidate.id}/resume`}
            {...(candidate.filename ? { download: candidate.filename } : {})}
            data-testid="resume-document-download-original"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-grad px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:shadow-md hover:brightness-110"
          >
            <Download size={14} aria-hidden />
            Download Original
          </a>
        </div>
      ) : null}

      {/*
        resume-doc-island
        Sole permitted `dangerouslySetInnerHTML` carve-out in the
        frontend (S-30e-8). `sanitizedHtml` is the cached output of
        `sanitizeResumeHtml(candidate.resume_html)` from the
        useMemo above — the structural test in
        `frontend/src/__tests__/no-dangerous-html.test.ts` enforces
        that this file contains BOTH a `sanitizeResumeHtml(` call
        AND the `dangerouslySetInnerHTML` attribute.
      */}
      <article
        className="resume-doc"
        data-testid="resume-document"
        aria-label={`${candidate.name} — Resume`}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
}
